import { DateTime } from 'luxon'

import CourseQ from '@/queries/CourseQ'
import SATSeriesQ from '@/queries/SATSeriesQ'
import SATTestQ from '@/queries/SATTestQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import type { Query } from '@/client/query'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { getSeriesTimeInfo } from '@/client/services/series'

import { getSeriesHostAvatarDetails } from '@/components/Host/helpers'

import type { SeriesBoxProps } from './types'

export default function getSeriesBoxProps({
  series,
  currentUserId,
}: {
  series: Query
  currentUserId: number | null
}): SeriesBoxProps {
  const id = series(SeriesQ.id) ?? 0
  const title = series(SeriesQ.title)
  const description = series(SeriesQ.description) ?? EMPTY_RICH_TEXT_VALUE
  const sessionCount = series(SeriesQ.countSessions()) ?? 0

  const course = series.one(SeriesQ.getCourse())
  const courseName = `${course(CourseQ.name)}`

  const satSeries = series.one(
    SATSeriesQ.where({
      include: [{ association: 'SATTest', required: true }],
      seriesId: SeriesQ.id,
    })
  )
  const satSeriesId = satSeries(SATSeriesQ.id)
  const satTestTitle = satSeries(SATTestQ.title) ?? ''

  const satScoreBandName = series(SeriesQ.getScoreBandName())

  const isSATSeries = satSeriesId != null
  const isRecurringTemplateSeries = series(SeriesQ.isRecurringTemplateSeries()) ?? false
  const showRecurringTimes = isSATSeries || isRecurringTemplateSeries

  const sessionStartDates = series
    .many(SeriesQ.getSessions())
    .map(session => DateTime.fromISO(`${session(SessionQ.start_date)}`))
  const timeInfo = getSeriesTimeInfo({ sessionStartDates })

  const isUserRegistered =
    currentUserId == null ? false : (series(SeriesQ.isUserRegistered(currentUserId)) ?? false)
  const isUserSelfReserved =
    currentUserId == null ? false : (series(SeriesQ.isUserSelfReserved(currentUserId)) ?? false)
  const isEnrollmentClosed = series(SeriesQ.isEnrollmentClosed()) ?? true

  const nextSession = series.one(SeriesQ.getNextSession())
  const nextSessionStart = DateTime.fromISO(`${nextSession(SessionQ.start_date)}`)
  const nextSessionEnd = DateTime.fromISO(`${nextSession(SessionQ.getComputedEndDate())}`)

  const hasEnded = series(SeriesQ.hasEnded()) ?? false
  const endedAtDateTime = DateTime.fromISO(`${series(SeriesQ.closed_at)}`)

  const hosts = getSeriesHostAvatarDetails(series)
  const numHosts = series(SeriesQ.countAllHosts({ approved: true })) ?? 1

  const pastSessionsCount = series(SeriesQ.countCompletedSessions()) ?? 0
  const registrationCount = series(SeriesQ.countRegistrations()) ?? 0
  const reservationCount = series(SeriesQ.countActiveReservations()) ?? 0
  const maxLearners = series(SeriesQ.max_learners) ?? 0

  return {
    id,
    title,
    description,
    sessionCount,
    courseName,
    satSeriesId,
    satTestTitle,
    satScoreBandName,
    timeInfo,
    isUserRegistered,
    isUserSelfReserved,
    isEnrollmentClosed,
    nextSessionStart,
    nextSessionEnd,
    hasEnded,
    endedAtDateTime,
    hosts,
    numHosts,
    pastSessionsCount,
    registrationCount,
    reservationCount,
    maxLearners,
    showRecurringTimes,
  }
}
